import type { CustomChore } from '../types'

/**
 * The persisted data model.
 *
 * Every synced row carries the same envelope as `../semainier`, for the same
 * reason: the sync engine is inherited wholesale and it reads `updatedAt`,
 * `deletedAt` and `dirty` on every row it touches.
 */

/** Sync envelope carried by every synced row. */
export interface SyncMeta {
  /** ISO timestamp. Assigned by Postgres once the row has been pushed. */
  updatedAt: string
  /** Soft delete. A hard delete could never propagate to the other device. */
  deletedAt: string | null
  /** Local edit not yet accepted by the server. */
  dirty: boolean
}

export interface Entity extends SyncMeta {
  id: string
}

/**
 * One ticked (or un-ticked) box: a day, a task, a person.
 *
 * `id` is deterministic — `${taskDate}:${taskId}:${memberId}`, composed by
 * `src/lib/completion-id.ts` — which is what makes the phone and the PC
 * ticking the same box converge on one row instead of two. The three
 * components are *also* stored as fields, and both halves matter: the id is
 * for convergence, the fields are for querying.
 *
 * Unchecking is `done: false`, never a delete. `deletedAt` exists only for
 * uniformity with the rest of the envelope.
 */
export interface Completion extends Entity {
  /** ISO date, `YYYY-MM-DD`, in the household's local calendar. */
  taskDate: string
  /**
   * A template id: a slug from `src/data/default-plan.ts`, or the `custom-…` id
   * of a `CustomTask`. Permanent either way — see CLAUDE.md §4.
   */
  taskId: string
  /** `household_members.id`, not a user id. */
  memberId: string
  done: boolean
}

/**
 * A chore the household added itself.
 *
 * The envelope and nothing else on top of `CustomChore` (`src/types.ts`), which
 * is the shape `buildPlan` reads. This is the first row in the app that is a
 * *definition* rather than a record of something that happened, and it is why
 * "the plan is code" is now only half true: the built-in rotation is still a
 * literal, and everything beside it is here.
 *
 * Removing one is a soft delete like everything else, and it deliberately
 * leaves its completions behind — `CLAUDE.md` §4. A chore deleted by mistake
 * would otherwise take its history with it.
 */
export type CustomTask = Entity & CustomChore

/**
 * One person in the household.
 *
 * A *member* is a person and the unit chores are assigned to; a *user* is a
 * login. They are separate rows on purpose (`README.md`, *What is stored*), and
 * the link between them — `household_members.user_id` — is **not on this
 * interface and never crosses the wire**. That absence is a security property:
 * a client that could write `user_id` could hand a stranger a seat in the house
 * without anybody proving control of an address. It is written server-side
 * only, by `claim_membership`, when an invitation is redeemed.
 *
 * `username` and `authEmail` are what let a member sign in on a device of their
 * own while the code lands in the family inbox — `src/lib/auth-email.ts` has
 * the reasoning. Both are empty for the owner, who signs in with their plain
 * address, and for a member who never gets a login.
 *
 * Removing a member is a soft delete like everything else, and it leaves their
 * completions behind for the reason `CLAUDE.md` §4 gives about removing a task:
 * a person removed by mistake would otherwise take a year of history with them.
 */
export interface Member extends Entity {
  displayName: string
  username: string
  authEmail: string
  /** `'parent' | 'child'`, editorial. Nothing branches on it — the owner of the
   *  household is `households.owner_user_id`, and that is what RLS checks. */
  role: string
}

/** A member on their way to the store, before they have a permanent id. */
export type NewMember = Pick<Member, 'displayName' | 'username' | 'authEmail' | 'role'>

/**
 * Reminder settings: one row per household, synced like everything else.
 *
 * It carries the envelope but is not a collection — a singleton with the fixed
 * id `settings` (`src/sync/schema.ts`), so that two devices cannot each create
 * their own "the" settings row.
 *
 * A fresh install starts `dirty: false` at the epoch (`defaults.ts`), which is
 * what stops a phone that has never opened *Réglages* from pushing its
 * defaults over the hour the household actually chose.
 */
export interface AppSettings extends SyncMeta {
  reminderEnabled: boolean
  reminderTime: string
  timezone: string
}

/** Everything that is persisted under `maison_zen_v1`. */
export interface AppData {
  completions: Completion[]
  tasks: CustomTask[]
  members: Member[]
  settings: AppSettings
}

/**
 * Synced collections, in the order the sync engine walks them.
 *
 * **Completions first, and the order is load-bearing.** `syncNow` walks this
 * list with `await` and aborts the whole pass on the first error, so a table
 * that is missing takes every collection after it down with it. `tasks` is the
 * newer table and therefore the one a deploy that skipped a migration will not
 * have; putting it last means such a project still pushes and pulls its
 * checkmarks — which is the property that outranks everything else here — and
 * reports the failure in the sync badge instead of quietly stopping.
 *
 * `members` is newer still, and last for a second reason on top of that one: it
 * is the only collection whose table refuses writes from most of the people who
 * hold it. Only the household's owner may write `household_members`, so a
 * child's device with a stale dirty member row would fail its push — and
 * putting that failure after the checkmarks means it costs a red badge instead
 * of a day of history.
 *
 * Nothing in the UI depends on the other order: a completion whose chore has
 * not arrived yet renders as nothing at all, because the day list is built from
 * the plan and the ticks are looked up against it; a chore assigned to a member
 * who has not arrived yet renders under an unnamed chip rather than not at all.
 */
export const COLLECTION_NAMES = ['completions', 'tasks', 'members'] as const

export type CollectionName = (typeof COLLECTION_NAMES)[number]
